"use client";

import EditIcon from "@mui/icons-material/Edit";
import HistoryIcon from "@mui/icons-material/History";
import { InputAdornment, TextField, Tooltip } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useFormikContext } from "formik";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import FormField from "@/components/atoms/FormField";
import MDBox from "@/components/atoms/MDBox";
import MDInput from "@/components/atoms/MDInput";
import MDTypography from "@/components/atoms/MDTypography";
import { getFieldWidthFromLabel } from "@/constants/unit-delivery-time.constants";
import { SkuFormData } from "@/components/screens/ProductFormPage/types";
import StockMovementModal from "@/components/templates/StockMovementModal";
import { Type } from "@/components/templates/StockMovementModal/form-data";
import useDialog from "@/hooks/use-dialog";
import useUnitDeliveryTime from "@/hooks/use-unit-delivery-time";
import { IStockMovement, IStockMovementInput } from "@/interfaces/sku-movement.interfaces";
import { SkuFormMessages } from "@/messages/sku/sku-form.messages";

import StockMovementHistoryModal from "../StockMovementHistoryModal";

type Props = {
  messages: SkuFormMessages;
  isEditingSku: boolean;
};

function Storage({ messages, isEditingSku }: Props) {
  const { values, handleChange, errors, setFieldValue, setFieldError } =
    useFormikContext<SkuFormData>();
  const { leadTimeLabel } = useUnitDeliveryTime();
  const leadTimeFieldWidthPx = useMemo(
    () => getFieldWidthFromLabel(leadTimeLabel),
    [leadTimeLabel]
  );

  const {
    isVisible: isStockMovementVisible,
    handleOpen: handleOpenStockMovement,
    handleClose: handleCloseStockMovement,
  } = useDialog<IStockMovementInput>();
  const {
    isVisible: isStockHistoryVisible,
    handleOpen: handleOpenStockHistory,
    handleClose: handleCloseStockHistory,
  } = useDialog<IStockMovement>();

  const calculateUpdatedStock = (currentStock: number, quantity: number, type: Type): number => {
    return type === Type.ENTRY ? currentStock + quantity : currentStock - quantity;
  };

  const handleSubmitStockMovement = async (_formData: IStockMovementInput) => {
    const { quantity, type } = _formData;
    const { stock, stockMovement } = values;

    let currentStock = typeof stock === "number" ? stock : parseFloat(stock as string);

    if (stockMovement) {
      const { quantity, type } = stockMovement;
      const stockMovementType = type === Type.ENTRY ? Type.OUT : Type.ENTRY;

      currentStock = calculateUpdatedStock(currentStock, quantity, stockMovementType);
    }

    const updatedStock = calculateUpdatedStock(currentStock, quantity, type);

    setFieldValue("stock", updatedStock);
    setFieldValue("stockMovement", _formData);
  };

  const formatWeight = useCallback((v: number | string | null | undefined) => {
    if (v === null || v === undefined || v === "") return "";
    const n = typeof v === "string" ? parseFloat(v.replace(",", ".")) : Number(v);
    return isNaN(n) || n < 0 ? "" : n.toFixed(3).replace(".", ",");
  }, []);

  const [weightText, setWeightText] = useState(() => formatWeight(values.weight));

  const handleWeightBlur = useCallback(() => {
    if (weightText === "") {
      setFieldValue("weight", "");
      return;
    }
    const parsed = parseFloat(weightText.replace(",", "."));
    if (isNaN(parsed) || parsed < 0) {
      setWeightText("");
      setFieldValue("weight", "");
      return;
    }
    const value = parseFloat(parsed.toFixed(3));
    setFieldValue("weight", value);
    setWeightText(formatWeight(value));
  }, [weightText, setFieldValue, formatWeight]);

  const handleIntegerChange =
    (field: "leadTime") => (event: React.ChangeEvent<HTMLInputElement>) => {
      const rawValue = event.target.value;

      if (rawValue === "") {
        setFieldValue(field, "");
        return;
      }

      const normalized = rawValue.replace(",", ".");
      const numeric = parseFloat(normalized);

      if (isNaN(numeric) || numeric < 0) {
        return;
      }

      const intValue = Math.floor(numeric);
      setFieldValue(field, intValue);
    };

  const handleDecimalChange =
    (field: "height" | "width" | "depth") =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const rawValue = event.target.value;

      if (rawValue === "") {
        setFieldValue(field, "");
        return;
      }

      const normalized = rawValue.replace(",", ".");
      const numeric = parseFloat(normalized);

      if (isNaN(numeric) || numeric < 0) {
        return;
      }

      setFieldValue(field, numeric);
      setFieldError(field, undefined);
    };

  const isValidDecimalValue = useCallback((value: number | string | null | undefined) => {
    if (value === null || value === undefined || value === "") return true;

    const numeric = typeof value === "string" ? parseFloat(value.replace(",", ".")) : Number(value);

    return !isNaN(numeric) && numeric >= 0;
  }, []);

  useEffect(() => {
    (["height", "width", "depth"] as const).forEach((field) => {
      if (errors[field] && isValidDecimalValue(values[field])) {
        setFieldError(field, undefined);
      }
    });
  }, [
    errors.height,
    errors.width,
    errors.depth,
    values.height,
    values.width,
    values.depth,
    isValidDecimalValue,
    setFieldError,
  ]);

  useEffect(() => {
    const weight = parseFloat(values.weight?.toString() || "0");
    const height = parseFloat(values.height?.toString() || "0");
    const width = parseFloat(values.width?.toString() || "0");
    const depth = parseFloat(values.depth?.toString() || "0");

    if (!isNaN(weight) && !isNaN(height) && !isNaN(width) && !isNaN(depth)) {
      const dimensionalWeight = (height * width * depth) / 5000;
      setFieldValue("dimensionalWeight", dimensionalWeight.toFixed(2));
    }
  }, [values.weight, values.height, values.width, values.depth, setFieldValue]);

  return (
    <MDBox p={3}>
      <MDBox display="flex">
        <MDTypography variant="h5" fontWeight={"bold"}>
          {messages.storageTitle}
        </MDTypography>
      </MDBox>
      <MDBox mt={1}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 12, md: 4 }}>
            <FormField
              variant="standard"
              name="weight"
              label={messages.weightLabel}
              value={weightText}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setWeightText(e.target.value)
              }
              onBlur={handleWeightBlur}
              fullWidth
              InputLabelProps={{ shrink: true }}
              error={!!errors.weight}
              type="text"
              inputProps={{ min: "0", step: "0.001", inputMode: "decimal" }}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 12, md: 4 }}>
            <FormField
              variant="standard"
              name="height"
              label={messages.heightLabel}
              value={values.height?.toString() || ""}
              onChange={handleDecimalChange("height")}
              fullWidth
              InputLabelProps={{ shrink: true }}
              error={!!errors.height}
              type="text"
              inputProps={{ min: "0", step: "0.001", inputMode: "decimal" }}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 12, md: 4 }}>
            <FormField
              variant="standard"
              name="width"
              label={messages.widthLabel}
              value={values.width?.toString() || ""}
              onChange={handleDecimalChange("width")}
              fullWidth
              InputLabelProps={{ shrink: true }}
              error={!!errors.width}
              type="text"
              inputProps={{ min: "0", step: "0.001", inputMode: "decimal" }}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 12, md: 4 }}>
            <FormField
              variant="standard"
              name="depth"
              label={messages.depthLabel}
              value={values.depth?.toString() || ""}
              onChange={handleDecimalChange("depth")}
              fullWidth
              InputLabelProps={{ shrink: true }}
              error={!!errors.depth}
              type="text"
              inputProps={{ min: "0", step: "0.001", inputMode: "decimal" }}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 12, md: 4 }}>
            <FormField
              variant="standard"
              name="dimensionalWeight"
              label={messages.dimensionLabel}
              value={values.dimensionalWeight?.toString() || ""}
              onChange={handleChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
              disabled
              error={!!errors.dimensionalWeight}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 12, md: 4 }}>
            <TextField
              label={messages.stockLabel}
              name="stock"
              type="number"
              value={values.stock?.toString() || ""}
              onChange={handleChange}
              error={!!errors.stock}
              fullWidth
              variant="standard"
              disabled
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Inserir movimentação">
                        <EditIcon
                          fontSize="small"
                          onClick={() => handleOpenStockMovement()}
                          style={{ cursor: "pointer" }}
                        />
                      </Tooltip>
                      {isEditingSku && (
                        <Tooltip title="Ver histórico">
                          <HistoryIcon
                            fontSize="small"
                            onClick={() => handleOpenStockHistory()}
                            style={{ cursor: "pointer", marginLeft: "8px" }}
                          />
                        </Tooltip>
                      )}
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Grid>

          <Grid size={12} sx={{ overflow: "visible" }}>
            <MDBox
              mb={1.5}
              sx={{
                width: leadTimeFieldWidthPx,
                minWidth: leadTimeFieldWidthPx,
                flexShrink: 0,
                overflow: "visible",
              }}
            >
              <MDInput
                variant="standard"
                name="leadTime"
                label={leadTimeLabel}
                value={values.leadTime?.toString() || ""}
                onChange={handleIntegerChange("leadTime")}
                fullWidth
                InputLabelProps={{
                  shrink: true,
                  sx: {
                    whiteSpace: "nowrap",
                    overflow: "visible",
                    textOverflow: "clip",
                    maxWidth: "none",
                  },
                }}
                sx={{
                  width: leadTimeFieldWidthPx,
                  "& .MuiInputLabel-root": {
                    whiteSpace: "nowrap",
                    overflow: "visible",
                    textOverflow: "clip",
                    maxWidth: "none",
                  },
                }}
                type="number"
                inputProps={{ min: "0", step: "1", inputMode: "numeric" }}
              />
            </MDBox>
          </Grid>

          <StockMovementModal
            open={isStockMovementVisible}
            onClose={handleCloseStockMovement}
            onSubmit={handleSubmitStockMovement}
            messages={messages}
            stockMovement={values.stockMovement}
          />

          <StockMovementHistoryModal
            open={isStockHistoryVisible}
            onClose={handleCloseStockHistory}
            messages={messages}
            skuId={values.id}
          />
        </Grid>
      </MDBox>
    </MDBox>
  );
}

export default Storage;
